/**
 * iter 34 Phase A.2 — Job Dash redesigned sections.
 *
 * Drop-in components added at the top of JobMiniPanel:
 *   <JobDashHero>          — editable special instructions, service speed,
 *                            billing ref, account number, service status.
 *   <SubjectAutocomplete>  — text-input subject picker (autocompletes against
 *                            org subjects, falls back to "create new on save").
 *   <JobLocationsList>     — multi-location list with priority/dwelling/phone
 *                            + inline "+ Add" picker.
 *
 * Phase B (per the user plan) wires Tasks + Activity Events on top of the
 * existing service_attempts schema. Phase C wires the side rail with Case
 * Info + the two new lookup tables.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr } from "../lib/api";
import {
  Plus, Trash, MagnifyingGlass, MapPin, Pencil, Check, X as XIcon,
  CheckSquare, Square, ChatCircleDots, ClockClockwise,
  File as FileIcon, FilePdf, FileImage, Upload, DownloadSimple,
  Eye, EyeSlash, EnvelopeSimple, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Drawer } from "./UI";
import CustomerSearchInput from "./CustomerSearchInput";
import UserSearchInput from "./UserSearchInput";


/* ------------------------------------------------------------------------ *
 * Reusable user inline-edit row (used for Attorney / Contact / Server).
 * Click chip to edit, opens debounced search box against /users?q=.
 * ------------------------------------------------------------------------ */
function UserInlineEdit({ label, userId, users, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const current = (users || []).find((u) => u.id === userId);
  const labelText = current
    ? `${current.name}${current.email ? ` · ${current.email}` : ""}`
    : "";

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <span className="label-xs w-28 flex-shrink-0">{label}</span>
      {editing ? (
        <div className="flex-1 flex items-center gap-2">
          <div className="flex-1">
            <UserSearchInput
              value={null}
              valueLabel={null}
              onSelect={async (uRow) => {
                try { await onSave(uRow.id); setEditing(false); }
                catch (e) { alert(formatErr(e)); }
              }}
              placeholder="Search by name or email…"
              testid={`${testid}-search`}
              autoFocus
            />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)} title="Cancel"><XIcon size={12} /></button>
        </div>
      ) : (
        <>
          <span
            className={`flex-1 text-sm cursor-pointer truncate ${labelText ? "" : "text-muted-foreground italic"}`}
            onClick={() => setEditing(true)}
            data-testid={`${testid}-value`}
          >
            {labelText || "Click to search and pick"}
          </span>
          {userId && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={async () => { try { await onSave(null); } catch (e) { alert(formatErr(e)); } }}
              title="Clear"
              data-testid={`${testid}-clear`}
            >
              <XIcon size={11} />
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)} title="Change"><Pencil size={11} /></button>
        </>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * Customer inline-edit — click chip to edit, opens debounced search box.
 * Powered by /customers?q= so the list works for orgs with hundreds of rows.
 * ------------------------------------------------------------------------ */
function CustomerInlineEdit({ customerId, customers, onSave, testid = "hero-customer" }) {
  const [editing, setEditing] = useState(false);
  const current = (customers || []).find((c) => c.id === customerId);
  const label = current
    ? `${current.name}${current.email ? ` · ${current.email}` : ""}`
    : "";

  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <span className="label-xs w-28 flex-shrink-0">CUSTOMER</span>
      {editing ? (
        <div className="flex-1 flex items-center gap-2">
          <div className="flex-1">
            <CustomerSearchInput
              value={null}
              valueLabel={null}
              onSelect={async (c) => {
                try { await onSave(c.id); setEditing(false); }
                catch (e) { alert(formatErr(e)); }
              }}
              placeholder="Search by name, email, or phone…"
              testid={`${testid}-search`}
              autoFocus
            />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)} title="Cancel"><XIcon size={12} /></button>
        </div>
      ) : (
        <>
          <span
            className={`flex-1 text-sm cursor-pointer truncate ${label ? "" : "text-muted-foreground italic"}`}
            onClick={() => setEditing(true)}
            data-testid={`${testid}-value`}
          >
            {label || "Click to search and pick a customer"}
          </span>
          {customerId && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={async () => { try { await onSave(null); } catch (e) { alert(formatErr(e)); } }}
              title="Clear customer"
              data-testid={`${testid}-clear`}
            >
              <XIcon size={11} />
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)} title="Change"><Pencil size={11} /></button>
        </>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * Inline-editable field. Click value to edit, Enter commits, Esc cancels.
 * ------------------------------------------------------------------------ */
function InlineEdit({ value, onSave, label, placeholder, testid, kind = "text", options }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  useEffect(() => { setVal(value ?? ""); }, [value]);
  const commit = async () => {
    if (val === (value ?? "")) { setEditing(false); return; }
    try { await onSave(val); setEditing(false); }
    catch (e) { alert(formatErr(e)); }
  };
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <span className="label-xs w-28 flex-shrink-0">{label}</span>
      {editing ? (
        <>
          {kind === "select" ? (
            <select className="input flex-1" value={val} onChange={(e) => setVal(e.target.value)} autoFocus>
              <option value="">—</option>
              {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : kind === "textarea" ? (
            <textarea className="input flex-1" rows={2} value={val}
              onChange={(e) => setVal(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Escape") { setVal(value ?? ""); setEditing(false); } }} />
          ) : (
            <input className="input flex-1" value={val} placeholder={placeholder || ""}
              onChange={(e) => setVal(e.target.value)} autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commit(); }
                if (e.key === "Escape") { setVal(value ?? ""); setEditing(false); }
              }} />
          )}
          <button className="btn btn-ghost btn-sm" onClick={commit} title="Save"><Check size={12} weight="bold" /></button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setVal(value ?? ""); setEditing(false); }} title="Cancel"><XIcon size={12} /></button>
        </>
      ) : (
        <>
          <span
            className={`flex-1 text-sm cursor-pointer truncate ${value ? "" : "text-muted-foreground italic"}`}
            onClick={() => setEditing(true)}
            data-testid={`${testid}-value`}
          >
            {value || (placeholder || "Click to edit")}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)} title="Edit"><Pencil size={11} /></button>
        </>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * Top hero — collapses the new dash columns into a compact 3-row strip
 * inside the existing drawer width.
 * ------------------------------------------------------------------------ */
export function JobDashHero({ job, customers, users, onSaveJob }) {
  const patch = (field) => (val) => onSaveJob({ [field]: val || null });

  return (
    <div className="surface p-3 space-y-2" data-testid="job-dash-hero">
      <div className="heading text-xs mb-2">JOB DASH</div>
      <CustomerInlineEdit
        customerId={job.customer_id}
        customers={customers}
        onSave={(id) => patch("customer_id")(id)}
        testid="hero-customer"
      />
      <UserInlineEdit
        label="ATTORNEY" testid="hero-attorney"
        userId={job.attorney_user_id} users={users}
        onSave={(id) => patch("attorney_user_id")(id)}
      />
      <UserInlineEdit
        label="CONTACT" testid="hero-contact"
        userId={job.contact_user_id} users={users}
        onSave={(id) => patch("contact_user_id")(id)}
      />
      <UserInlineEdit
        label="SERVER" testid="hero-server"
        userId={job.server_user_id} users={users}
        onSave={(id) => patch("server_user_id")(id)}
      />
      <InlineEdit
        label="SPEED" testid="hero-speed" kind="select" options={["Routine", "Standard", "Rush", "Same-Day"]}
        value={job.service_speed} onSave={patch("service_speed")}
      />
      <InlineEdit label="STATUS" testid="hero-status" kind="select"
        options={["Pending", "Active", "Served", "Non-Service", "On Hold"]}
        value={job.service_status} onSave={patch("service_status")}
      />
      <InlineEdit label="BILLING REF" testid="hero-billing-ref"
        value={job.billing_ref} onSave={patch("billing_ref")} placeholder="e.g. SMITH-v-DART" />
      <InlineEdit label="ACCOUNT #" testid="hero-account"
        value={job.account_number} onSave={patch("account_number")} placeholder="e.g. 100" />
      <InlineEdit label="SPECIAL INSTR." testid="hero-special-instructions" kind="textarea"
        value={job.special_instructions} onSave={patch("special_instructions")} />
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * Subject Name — autocomplete text field
 * ------------------------------------------------------------------------ */
export function SubjectAutocomplete({ value, onSelect, testid = "subject-autocomplete" }) {
  const [q, setQ] = useState(value || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef();

  useEffect(() => { setQ(value || ""); }, [value]);

  const runSearch = (text) => {
    api.get(`/subjects/search?q=${encodeURIComponent(text)}&limit=8`)
      .then((r) => setOpts(r.data))
      .catch(() => setOpts([]));
  };

  const onChange = (text) => {
    setQ(text);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(text), 180);
  };

  return (
    <div className="relative" data-testid={testid}>
      <div className="flex items-center gap-2">
        <MagnifyingGlass size={12} className="text-muted-foreground absolute left-2 top-2.5" />
        <input
          className="input pl-7"
          placeholder="Subject name… (type to search)"
          value={q}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => { setOpen(true); runSearch(q); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          data-testid={`${testid}-input`}
        />
      </div>
      {open && opts.length > 0 && (
        <div className="absolute z-30 w-full mt-1 surface max-h-64 overflow-y-auto" data-testid={`${testid}-menu`}>
          {opts.map((s) => (
            <button
              key={s.id}
              type="button"
              className="w-full text-left p-2 hover:bg-muted/40 border-b border-border last:border-b-0"
              onMouseDown={() => { setQ(s.name); setOpen(false); onSelect(s); }}
              data-testid={`${testid}-option-${s.id}`}
            >
              <div className="text-sm font-medium">{s.name}</div>
              {s.aka && <div className="text-[10px] mono text-muted-foreground">aka {s.aka}</div>}
            </button>
          ))}
          {q && !opts.some((o) => o.name.toLowerCase() === q.toLowerCase()) && (
            <div className="p-2 text-xs text-muted-foreground italic border-t border-border" data-testid={`${testid}-create-hint`}>
              No match — &quot;<strong>{q}</strong>&quot; will be created as a new subject on save.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * Job Locations multi-list
 * ------------------------------------------------------------------------ */
export function JobLocationsList({ jobId, allLocations }) {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ location_id: "", type_of_dwelling: "", phone: "", priority: 1 });
  const [err, setErr] = useState("");

  const load = () => api.get(`/jobs/${jobId}/locations`).then((r) => setRows(r.data)).catch((e) => setErr(formatErr(e)));
  useEffect(() => { if (jobId) load(); /* eslint-disable-next-line */ }, [jobId]);

  const add = async () => {
    if (!form.location_id) return;
    setErr("");
    try {
      await api.post(`/jobs/${jobId}/locations`, form);
      setAdding(false);
      setForm({ location_id: "", type_of_dwelling: "", phone: "", priority: 1 });
      load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const del = async (jl) => {
    if (!confirm(`Remove "${jl.location?.name}" from this job?`)) return;
    try { await api.delete(`/jobs/${jobId}/locations/${jl.id}`); load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const reorder = async (jl, dir) => {
    const i = rows.findIndex((r) => r.id === jl.id);
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const a = rows[i], b = rows[j];
    try {
      await api.patch(`/jobs/${jobId}/locations/${a.id}`, { order_index: b.order_index });
      await api.patch(`/jobs/${jobId}/locations/${b.id}`, { order_index: a.order_index });
      load();
    } catch (e) { alert(formatErr(e)); }
  };

  const used = useMemo(() => new Set(rows.map((r) => r.location_id)), [rows]);
  const available = (allLocations || []).filter((l) => !used.has(l.id));

  return (
    <div className="space-y-2" data-testid="job-locations-list">
      {rows.length === 0 && !adding && (
        <div className="text-xs text-muted-foreground italic" data-testid="job-locations-empty">
          No locations on this job yet.
        </div>
      )}
      {rows.map((jl, idx) => (
        <div key={jl.id} className="surface p-2 flex items-start gap-2" data-testid={`job-location-row-${jl.id}`}>
          <div className="flex flex-col gap-0.5">
            <button className="text-xs hover:text-primary disabled:opacity-30"
              onClick={() => reorder(jl, -1)} disabled={idx === 0} title="Move up">▲</button>
            <span className="text-[10px] text-muted-foreground text-center">{idx + 1}</span>
            <button className="text-xs hover:text-primary disabled:opacity-30"
              onClick={() => reorder(jl, 1)} disabled={idx === rows.length - 1} title="Move down">▼</button>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <MapPin size={12} className="text-muted-foreground flex-shrink-0" />
              <span className="font-medium text-sm truncate">{jl.location?.name || "(missing)"}</span>
              <span className="text-[10px] mono text-muted-foreground">P{jl.priority}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {[jl.location?.address1, jl.location?.city, jl.location?.state].filter(Boolean).join(", ")}
            </div>
            {(jl.type_of_dwelling || jl.phone) && (
              <div className="text-xs mono mt-1 space-x-2">
                {jl.type_of_dwelling && <span>🏠 {jl.type_of_dwelling}</span>}
                {jl.phone && <span>☎ {jl.phone}</span>}
              </div>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => del(jl)} data-testid={`job-location-delete-${jl.id}`}>
            <Trash size={12} />
          </button>
        </div>
      ))}

      {adding ? (
        <div className="surface p-3 space-y-2" data-testid="job-location-add-form">
          <select className="input" value={form.location_id}
            onChange={(e) => setForm({ ...form, location_id: e.target.value })}
            data-testid="job-location-add-select">
            <option value="">Pick a location…</option>
            {available.map((l) => <option key={l.id} value={l.id}>{l.name}{l.city ? ` — ${l.city}` : ""}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input className="input" placeholder="Dwelling type" value={form.type_of_dwelling}
              onChange={(e) => setForm({ ...form, type_of_dwelling: e.target.value })} />
            <input className="input" placeholder="Phone" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>CANCEL</button>
            <button className="btn btn-primary btn-sm" onClick={add} disabled={!form.location_id} data-testid="job-location-add-save">ADD</button>
          </div>
          {err && <div className="text-xs text-destructive">{err}</div>}
        </div>
      ) : (
        <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)} data-testid="job-location-add-btn">
          <Plus size={12} /> ADD LOCATION
        </button>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * iter 34 Phase B — Tasks block (per-job).
 * GET/POST /jobs/{id}/tasks · PATCH/DELETE /jobs/{id}/tasks/{tid}
 * ------------------------------------------------------------------------ */
const PRIORITY_OPTS = ["low", "medium", "high"];
const PRIORITY_CLASS = {
  low: "text-muted-foreground",
  medium: "text-amber-600",
  high: "text-destructive font-medium",
};

export function JobTasksBlock({ jobId }) {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", priority: "medium" });
  const [err, setErr] = useState("");

  const load = () => api.get(`/jobs/${jobId}/tasks`).then(r => setRows(r.data)).catch(e => setErr(formatErr(e)));
  useEffect(() => { if (jobId) load(); /* eslint-disable-next-line */ }, [jobId]);

  const add = async () => {
    if (!form.title.trim()) return;
    setErr("");
    try {
      await api.post(`/jobs/${jobId}/tasks`, { title: form.title.trim(), priority: form.priority });
      setForm({ title: "", priority: "medium" });
      setAdding(false);
      load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const toggle = async (t) => {
    const next = t.status === "done" ? "todo" : "done";
    try { await api.patch(`/jobs/${jobId}/tasks/${t.id}`, { status: next }); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const del = async (t) => {
    if (!confirm(`Delete task "${t.title}"?`)) return;
    try { await api.delete(`/jobs/${jobId}/tasks/${t.id}`); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const open = rows.filter(r => r.status !== "done");
  const done = rows.filter(r => r.status === "done");

  return (
    <div className="space-y-2" data-testid="job-tasks-block">
      {rows.length === 0 && !adding && (
        <div className="text-xs text-muted-foreground italic" data-testid="job-tasks-empty">
          No tasks on this job yet.
        </div>
      )}

      {open.map(t => (
        <TaskRow key={t.id} task={t} onToggle={toggle} onDelete={del} />
      ))}

      {done.length > 0 && (
        <details className="text-xs" data-testid="job-tasks-done-group">
          <summary className="cursor-pointer text-muted-foreground py-1">
            Completed ({done.length})
          </summary>
          <div className="space-y-1 mt-1">
            {done.map(t => (
              <TaskRow key={t.id} task={t} onToggle={toggle} onDelete={del} />
            ))}
          </div>
        </details>
      )}

      {adding ? (
        <div className="surface p-2 space-y-2" data-testid="job-task-add-form">
          <input
            className="input"
            placeholder="Task title…"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            autoFocus
            data-testid="job-task-add-title"
          />
          <div className="flex items-center gap-2">
            <select className="input flex-1" value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              data-testid="job-task-add-priority">
              {PRIORITY_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>CANCEL</button>
            <button className="btn btn-primary btn-sm" onClick={add} disabled={!form.title.trim()} data-testid="job-task-add-save">
              ADD
            </button>
          </div>
          {err && <div className="text-xs text-destructive">{err}</div>}
        </div>
      ) : (
        <button className="btn btn-secondary btn-sm" onClick={() => setAdding(true)} data-testid="job-task-add-btn">
          <Plus size={12} /> ADD TASK
        </button>
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onDelete }) {
  const done = task.status === "done";
  return (
    <div className="flex items-start gap-2 p-1.5 hover:bg-muted/30 border border-border rounded" data-testid={`job-task-row-${task.id}`}>
      <button
        type="button"
        className="mt-0.5 flex-shrink-0 hover:text-primary"
        onClick={() => onToggle(task)}
        title={done ? "Mark not done" : "Mark done"}
        data-testid={`job-task-toggle-${task.id}`}
      >
        {done ? <CheckSquare size={14} weight="fill" /> : <Square size={14} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${done ? "line-through text-muted-foreground" : ""}`}>{task.title}</div>
        <div className="text-[10px] mono flex gap-2 mt-0.5">
          <span className={PRIORITY_CLASS[task.priority] || ""}>{task.priority}</span>
          {task.created_at && (
            <span className="text-muted-foreground">{new Date(task.created_at).toLocaleDateString()}</span>
          )}
        </div>
      </div>
      <button className="btn btn-ghost btn-sm flex-shrink-0" onClick={() => onDelete(task)} data-testid={`job-task-delete-${task.id}`}>
        <Trash size={11} />
      </button>
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * iter 34 Phase B — Activity Events log (per-job).
 * GET /jobs/{id}/activity · POST /jobs/{id}/activity   (free-form comments)
 * ------------------------------------------------------------------------ */
function _describeActivity(ev) {
  // System events store a JSON-string description. Comments use {"text": "..."}.
  if (!ev.description) return "";
  try {
    const j = JSON.parse(ev.description);
    if (j && typeof j === "object") {
      if (j.text) return j.text;
      // Generic key-value summary for audit events
      return Object.entries(j).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(", ");
    }
  } catch (_) { /* not JSON, fall through */ }
  return ev.description;
}

export function JobActivityLog({ jobId, refreshTick = 0 }) {
  const [rows, setRows] = useState([]);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => api.get(`/jobs/${jobId}/activity?limit=50`).then(r => setRows(r.data)).catch(e => setErr(formatErr(e)));
  useEffect(() => { if (jobId) load(); /* eslint-disable-next-line */ }, [jobId, refreshTick]);

  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/jobs/${jobId}/activity`, { comment: comment.trim() });
      setComment("");
      load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="job-activity-log">
      <div className="space-y-1 max-h-56 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="text-xs text-muted-foreground italic" data-testid="job-activity-empty">
            No activity yet.
          </div>
        ) : rows.map(ev => (
          <div key={ev.id} className="flex items-start gap-2 py-1 border-b border-border last:border-0" data-testid={`job-activity-row-${ev.id}`}>
            <span className="text-muted-foreground mt-0.5 flex-shrink-0">
              {ev.type === "comment" ? <ChatCircleDots size={11} /> : <ClockClockwise size={11} />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm whitespace-pre-wrap break-words">{_describeActivity(ev)}</div>
              <div className="text-[10px] mono text-muted-foreground mt-0.5">
                {ev.user_name || "system"} · {ev.created_at ? new Date(ev.created_at).toLocaleString() : ""}
                {ev.type !== "comment" && <span className="ml-2 uppercase">[{ev.type}]</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder="Add a comment to the activity log…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addComment(); } }}
          data-testid="job-activity-comment-input"
        />
        <button className="btn btn-primary btn-sm" onClick={addComment} disabled={busy || !comment.trim()} data-testid="job-activity-comment-btn">
          <Plus size={12} /> POST
        </button>
      </div>
      {err && <div className="text-xs text-destructive">{err}</div>}
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * iter 34 Phase C — Documents block (per-job, multi-type).
 *
 *  All uploaded files for the job live in one `documents` table. The
 *  doc_type column (service/misc/invoice/affidavit/other) is the
 *  discriminator. We expose a small tab strip to filter by type.
 *
 *  GET    /jobs/{id}/documents?doc_type=...
 *  POST   /jobs/{id}/documents   (multipart: file, doc_type, title)
 *  PATCH  /documents/{id}        ({doc_type, title})
 *  DELETE /documents/{id}
 * ------------------------------------------------------------------------ */
const DOC_TABS = [
  { key: "all", label: "ALL" },
  { key: "service", label: "SERVICE" },
  { key: "misc", label: "MISC" },
  { key: "invoice", label: "INVOICE" },
  { key: "affidavit", label: "AFFIDAVIT" },
  { key: "other", label: "OTHER" },
];

function _fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function _docIcon(mime) {
  if (!mime) return FileIcon;
  if (mime.startsWith("image/")) return FileImage;
  if (mime === "application/pdf") return FilePdf;
  return FileIcon;
}

/* iter 34 Phase D follow-up — "Sent (n)" badge with hover preview. */
function DocSentBadge({ doc }) {
  const [history, setHistory] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadHistory = () => {
    if (history || loading) return;
    setLoading(true);
    api.get(`/documents/${doc.id}/outbound-emails`)
      .then(r => setHistory(r.data))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  };

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => { setOpen(true); loadHistory(); }}
      onMouseLeave={() => setOpen(false)}
      data-testid={`job-document-sent-badge-${doc.id}`}
    >
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
        <PaperPlaneTilt size={9} />
        Sent ({doc.sent_count})
      </span>
      {open && (
        <span
          className="absolute z-50 left-0 top-full mt-1 surface p-2 shadow-lg text-[11px] mono whitespace-nowrap"
          style={{ minWidth: 240 }}
          data-testid={`job-document-sent-history-${doc.id}`}
        >
          {loading ? (
            <span className="text-muted-foreground">Loading…</span>
          ) : !history || history.length === 0 ? (
            <span className="text-muted-foreground italic">No send history.</span>
          ) : (
            <span className="block space-y-1">
              <span className="block label-xs mb-1">RECENT SENDS</span>
              {history.slice(0, 5).map(h => (
                <span key={h.id} className="block">
                  <span className="font-medium text-foreground">{h.to_email}</span>
                  <span className="text-muted-foreground">
                    {" · "}
                    {h.sent_at ? new Date(h.sent_at).toLocaleString() : "—"}
                    {h.sent_via && ` · ${h.sent_via}`}
                    {h.status !== "sent" && ` · ${h.status}`}
                  </span>
                </span>
              ))}
              {history.length > 5 && (
                <span className="block text-muted-foreground italic">+{history.length - 5} more…</span>
              )}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

export function JobDocumentsBlock({ jobId, onActivityChange }) {
  const [rows, setRows] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [uploadType, setUploadType] = useState("service");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [emailDoc, setEmailDoc] = useState(null);  // when set, EmailDocumentDrawer opens
  const fileRef = useRef(null);

  const load = () => api.get(`/jobs/${jobId}/documents`).then(r => setRows(r.data)).catch(e => setErr(formatErr(e)));
  useEffect(() => { if (jobId) load(); /* eslint-disable-next-line */ }, [jobId]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    for (const t of DOC_TABS) if (t.key !== "all") c[t.key] = 0;
    for (const r of rows) c[r.doc_type] = (c[r.doc_type] || 0) + 1;
    return c;
  }, [rows]);

  const visible = activeTab === "all" ? rows : rows.filter(r => r.doc_type === activeTab);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", uploadType);
      fd.append("title", f.name);
      await api.post(`/jobs/${jobId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
      if (onActivityChange) onActivityChange();
    } catch (ex) { setErr(formatErr(ex)); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const changeType = async (doc, newType) => {
    if (newType === doc.doc_type) return;
    try { await api.patch(`/documents/${doc.id}`, { doc_type: newType }); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  const toggleVisibility = async (doc) => {
    try {
      await api.patch(`/documents/${doc.id}`, { is_visible_to_client: !doc.is_visible_to_client });
      load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const del = async (doc) => {
    if (!confirm(`Delete "${doc.title || doc.filename}"?`)) return;
    try { await api.delete(`/documents/${doc.id}`); load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  return (
    <div className="space-y-2" data-testid="job-documents-block">
      {/* Tab strip */}
      <div className="flex flex-wrap gap-1" data-testid="job-documents-tabs">
        {DOC_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`btn btn-sm ${activeTab === t.key ? "btn-primary" : "btn-ghost"}`}
            data-testid={`job-documents-tab-${t.key}`}
          >
            {t.label} <span className="ml-1 text-[10px] opacity-70">({counts[t.key] || 0})</span>
          </button>
        ))}
      </div>

      {/* Upload control */}
      <div className="surface p-2 flex flex-wrap items-center gap-2" data-testid="job-documents-upload-row">
        <span className="label-xs">UPLOAD AS</span>
        <select className="input" style={{ maxWidth: 140 }} value={uploadType}
          onChange={(e) => setUploadType(e.target.value)} data-testid="job-documents-upload-type">
          {DOC_TABS.filter(t => t.key !== "all").map(t =>
            <option key={t.key} value={t.key}>{t.label.toLowerCase()}</option>
          )}
        </select>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={onUpload}
          accept="image/*,application/pdf"
          data-testid="job-documents-upload-input"
        />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="job-documents-upload-btn"
        >
          <Upload size={12} /> {uploading ? "UPLOADING…" : "CHOOSE FILE"}
        </button>
        <span className="text-[10px] text-muted-foreground">PDF or image, ≤10 MB</span>
      </div>

      {/* List */}
      {visible.length === 0 ? (
        <div className="text-xs text-muted-foreground italic" data-testid="job-documents-empty">
          {rows.length === 0
            ? "No documents on this job yet."
            : `No documents in "${activeTab}".`}
        </div>
      ) : (
        <div className="space-y-1">
          {visible.map(d => {
            const Icon = _docIcon(d.mime_type);
            return (
              <div key={d.id} className="flex items-start gap-2 p-2 border border-border rounded hover:bg-muted/30" data-testid={`job-document-row-${d.id}`}>
                <Icon size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <a href={d.url} target="_blank" rel="noopener noreferrer"
                    className="text-sm font-medium hover:text-primary truncate block"
                    title={d.title || d.filename}
                    data-testid={`job-document-link-${d.id}`}>
                    {d.title || d.filename || "(untitled)"}
                  </a>
                  <div className="text-[10px] mono text-muted-foreground flex items-center gap-2">
                    <span>{_fmtBytes(d.size_bytes)}</span>
                    {d.created_at && <span>{new Date(d.created_at).toLocaleDateString()}</span>}
                    {d.sent_count > 0 && <DocSentBadge doc={d} />}
                  </div>
                </div>
                <select
                  className="input"
                  style={{ maxWidth: 110, fontSize: 11 }}
                  value={d.doc_type}
                  onChange={(e) => changeType(d, e.target.value)}
                  data-testid={`job-document-type-${d.id}`}
                >
                  {DOC_TABS.filter(t => t.key !== "all").map(t =>
                    <option key={t.key} value={t.key}>{t.label.toLowerCase()}</option>
                  )}
                </select>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => toggleVisibility(d)}
                  title={d.is_visible_to_client ? "Visible to client — click to hide" : "Hidden from client — click to share"}
                  data-testid={`job-document-visibility-${d.id}`}
                >
                  {d.is_visible_to_client
                    ? <Eye size={12} weight="fill" className="text-green-600" />
                    : <EyeSlash size={12} className="text-muted-foreground" />}
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEmailDoc(d)}
                  title="Email this document using a template"
                  data-testid={`job-document-email-${d.id}`}
                >
                  <EnvelopeSimple size={12} />
                </button>
                <a className="btn btn-ghost btn-sm" href={d.url} target="_blank" rel="noopener noreferrer" title="Open">
                  <DownloadSimple size={12} />
                </a>
                <button className="btn btn-ghost btn-sm" onClick={() => del(d)} data-testid={`job-document-delete-${d.id}`} title="Delete">
                  <Trash size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {err && <div className="text-xs text-destructive" data-testid="job-documents-error">{err}</div>}

      <EmailDocumentDrawer
        jobId={jobId}
        doc={emailDoc}
        onClose={() => setEmailDoc(null)}
        onSent={() => {
          setEmailDoc(null);
          load();  // refresh sent_count badge
          if (onActivityChange) onActivityChange();
        }}
      />
    </div>
  );
}


/* ------------------------------------------------------------------------ *
 * iter 34 Phase D — Email Document drawer.
 *
 * Opens stacked on top of JobMiniPanel. Loads the resolved email template
 * (by doc_type → org override → system default) via
 *   GET /api/jobs/{job_id}/documents/{doc_id}/email-preview
 * Pre-fills To / Subject / Body. User can edit any field. On send:
 *   POST /api/jobs/{job_id}/documents/{doc_id}/send-email
 * which dispatches through Gmail→SendGrid fallback with the doc attached
 * and threads the message into any existing inbound email_intake for the
 * job.
 * ------------------------------------------------------------------------ */
function EmailDocumentDrawer({ jobId, doc, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ to_email: "", cc_email: "", subject: "", body: "" });
  const [recipientChoice, setRecipientChoice] = useState("customer"); // "customer" | "attorney" | "custom"
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const loadPreview = (d) => {
    setLoading(true);
    api.get(`/jobs/${jobId}/documents/${d.id}/email-preview`)
      .then(r => {
        setPreview(r.data);
        setForm({
          to_email: r.data.to_email || "",
          cc_email: "",
          subject: r.data.subject || "",
          body: r.data.body || "",
        });
        const rec = r.data.template?.default_recipient;
        setRecipientChoice(rec === "attorney" ? "attorney" : "customer");
      })
      .catch(e => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setDone(null);
    setErr("");
    if (!doc) { setPreview(null); return; }
    loadPreview(doc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, jobId]);

  const onRecipientSwitch = (choice) => {
    setRecipientChoice(choice);
    if (!preview) return;
    if (choice === "customer") setForm(f => ({ ...f, to_email: preview.suggested_customer_email || "" }));
    else if (choice === "attorney") setForm(f => ({ ...f, to_email: preview.suggested_attorney_email || "" }));
    // "custom" leaves the current value alone
  };

  const send = async () => {
    if (!form.to_email.trim() || !form.subject.trim() || !form.body.trim()) {
      setErr("To, Subject, and Body are required.");
      return;
    }
    setSending(true); setErr("");
    try {
      const r = await api.post(`/jobs/${jobId}/documents/${doc.id}/send-email`, {
        to_email: form.to_email.trim(),
        cc_email: form.cc_email.trim() || null,
        subject: form.subject,
        body: form.body,
        template_id: preview?.template?.id || null,
      });
      setDone(r.data);
      setTimeout(() => { if (onSent) onSent(r.data); }, 1200);
    } catch (e) { setErr(formatErr(e)); }
    finally { setSending(false); }
  };

  return (
    <Drawer
      open={!!doc}
      onClose={onClose}
      title={doc ? `Email: ${doc.title || doc.filename}` : "Email Document"}
      testid="email-document-drawer"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending} data-testid="email-doc-cancel">CANCEL</button>
          <button className="btn btn-primary" onClick={send} disabled={sending || !!done || loading} data-testid="email-doc-send">
            <PaperPlaneTilt size={14} /> {sending ? "SENDING…" : (done ? "SENT" : "SEND")}
          </button>
        </>
      }
    >
      {loading ? (
        <div className="py-10 text-center text-muted-foreground">Loading template…</div>
      ) : !preview ? (
        <div className="py-10 text-center text-muted-foreground">No template available.</div>
      ) : (
        <div className="space-y-3" data-testid="email-document-form">
          <div className="surface p-2">
            <div className="text-[10px] mono text-muted-foreground mb-1">TEMPLATE</div>
            <div className="text-sm font-medium" data-testid="email-doc-template-name">{preview.template.name}</div>
            <div className="text-[10px] text-muted-foreground italic">
              {preview.template.is_system_default ? "System default — admins can override under Settings" : "Org override"}
              {" · doc_type: "}{preview.template.doc_type}
            </div>
          </div>

          <div className="surface p-2 flex items-center gap-2 text-xs">
            <FileIcon size={14} className="text-muted-foreground" />
            <span className="font-medium truncate flex-1">{doc?.title || doc?.filename}</span>
            <span className="mono text-[10px] text-muted-foreground">{doc?.mime_type || ""}</span>
          </div>

          <div>
            <div className="label-xs mb-1">RECIPIENT</div>
            <div className="flex gap-1 mb-2" data-testid="email-doc-recipient-tabs">
              <button
                type="button"
                className={`btn btn-sm ${recipientChoice === "customer" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => onRecipientSwitch("customer")}
                data-testid="email-doc-recipient-customer"
                disabled={!preview.suggested_customer_email}
                title={preview.suggested_customer_email || "No customer email on file"}
              >CUSTOMER</button>
              <button
                type="button"
                className={`btn btn-sm ${recipientChoice === "attorney" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => onRecipientSwitch("attorney")}
                data-testid="email-doc-recipient-attorney"
                disabled={!preview.suggested_attorney_email}
                title={preview.suggested_attorney_email || "No attorney email on file"}
              >ATTORNEY</button>
              <button
                type="button"
                className={`btn btn-sm ${recipientChoice === "custom" ? "btn-primary" : "btn-ghost"}`}
                onClick={() => onRecipientSwitch("custom")}
                data-testid="email-doc-recipient-custom"
              >CUSTOM</button>
            </div>
            <input
              className="input"
              placeholder="To email"
              value={form.to_email}
              onChange={(e) => setForm({ ...form, to_email: e.target.value })}
              data-testid="email-doc-to"
            />
          </div>

          <input
            className="input"
            placeholder="CC (optional)"
            value={form.cc_email}
            onChange={(e) => setForm({ ...form, cc_email: e.target.value })}
            data-testid="email-doc-cc"
          />

          <input
            className="input"
            placeholder="Subject"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            data-testid="email-doc-subject"
          />

          <textarea
            className="input"
            rows={12}
            placeholder="Body"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            data-testid="email-doc-body"
          />

          {done && (
            <div className="surface p-2 text-xs" data-testid="email-doc-success">
              <div className="font-medium text-green-600">✓ Sent via {done.sent_via}</div>
              <div className="text-muted-foreground mt-1">From: {done.from_email}</div>
              {done.threaded && (
                <div className="text-muted-foreground">Threaded into existing email conversation.</div>
              )}
            </div>
          )}

          {err && <div className="text-xs text-destructive" data-testid="email-doc-error">{err}</div>}
        </div>
      )}
    </Drawer>
  );
}


